import React, { ReactElement, useContext } from "react";
import { Divider, Grid, IconButton, ListItemIcon, Menu, MenuItem, Typography, Button } from "@mui/material";
import { AccountCircle, Settings, Payment, History, Login, PersonAdd, Logout, TrendingUp } from "@mui/icons-material";
import { ContextPlayer } from "../contexts/PlayerContext";
import { useSignOut } from "../auth/useAuthentication";
import { AuthState, UserContext } from "../contexts/UserContext";
import { useLocation } from "react-router-dom";
import { PlanStatus, PlanType } from "../api/models/User";

interface UserMenuProps {
    player?: ContextPlayer;
    onSettingsClicked: () => void;
    onSignInClicked: () => void;
    onSignUpClicked: () => void;
    onSignOutClicked: () => void;
    onManageSubscriptionClicked: () => void;
    onRecentRoomsClicked: () => void;
    onSubscribeClicked: () => void;
    onAlignmentTrendClicked: (roomId: string) => void;
}

export function UserMenu(props: UserMenuProps): ReactElement {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { authState, user } = useContext(UserContext);
    const [signOut] = useSignOut();
    const location = useLocation();

    // Extract room ID from URL path
    // URL format: /{roomUrlId} or /{roomUrlId}/trend or /{roomUrlId}/settings
    const roomUrlId = React.useMemo(() => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        // Room URL IDs are 8 characters, so check if first part matches that pattern
        if (pathParts.length > 0 && pathParts[0].length === 8) {
            return pathParts[0];
        }
        return undefined;
    }, [location.pathname]);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSettingsClicked = () => {
        handleCloseMenu();
        props.onSettingsClicked();
    };

    const handleSignInClicked = () => {
        handleCloseMenu();
        props.onSignInClicked();
    };

    const handleSignUpClicked = () => {
        handleCloseMenu();
        props.onSignUpClicked();
    };

    const handleSignOutClicked = () => {
        handleCloseMenu();
        //TODO pri2: nice if signout could be handeled outside the menu
        signOut();
        props.onSignOutClicked();
    };

    const handleManageSubscriptionClicked = () => {
        handleCloseMenu();
        props.onManageSubscriptionClicked();
    };

    const handleRecentRoomsClicked = () => {
        handleCloseMenu();
        props.onRecentRoomsClicked();
    };

    const handleSubscribeClicked = () => {
        handleCloseMenu();
        props.onSubscribeClicked();
    };

    const handleAlignmentTrendClicked = () => {
        handleCloseMenu();
        if (roomUrlId) {
            props.onAlignmentTrendClicked(roomUrlId);
        }
    };

    const avatarSrc = (playerName: string) => {
        return "https://api.dicebear.com/7.x/bottts/svg?seed=" + playerName;
    };

    // Determine if subscribe button should be shown (same logic as SubscriptionRibbon)
    const shouldShowSubscribe = () => {
        if (!user) return false;
        
        const plan = user.plan;
        const isPremiumCanceled = plan.type === PlanType.Premium && plan.status === PlanStatus.Canceled;
        const isTrial = plan.type === PlanType.Trial;
        
        return isTrial || isPremiumCanceled;
    };

    return (
        <div>
            <Grid container spacing={0} direction="row" justifyContent="center" alignItems="center">
                <Grid>
                    <Typography sx={{ color: "primary.contrastText" }} variant="body1">
                        {" "}
                        {props.player?.name ? props.player.name : user?.email}
                    </Typography>
                </Grid>
                <Grid>
                    <IconButton aria-label="account of current user" aria-controls="menu-appbar" aria-haspopup="true" onClick={handleMenu} color="inherit">
                        {authState == AuthState.SignedIn && props.player ? (
                            <img src={avatarSrc(props.player.name)} alt="" width={24} height={24} />
                        ) : (
                            <AccountCircle />
                        )}
                    </IconButton>
                </Grid>

                <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    open={open}
                    onClose={handleCloseMenu}
                >
                    {props.player?.isModerator && (
                        <MenuItem onClick={handleSettingsClicked}>
                            <ListItemIcon>
                                <Settings fontSize="small" />
                            </ListItemIcon>
                            <Typography color="textPrimary">Configure Card Deck</Typography>
                        </MenuItem>
                    )}
                    {user?.plan.subscription && (
                        <MenuItem onClick={handleManageSubscriptionClicked}>
                            <ListItemIcon>
                                <Payment fontSize="small" />
                            </ListItemIcon>
                            <Typography color="textPrimary">Manage Subscription</Typography>
                        </MenuItem>
                    )}

                    {authState == AuthState.SignedIn && (
                        <>
                        {roomUrlId && (
                            <MenuItem onClick={handleAlignmentTrendClicked}>
                                <ListItemIcon>
                                    <TrendingUp fontSize="small" />
                                </ListItemIcon>
                                <Typography color="textPrimary">Alignment Trends</Typography>
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleRecentRoomsClicked}>
                            <ListItemIcon>
                                <History fontSize="small" />
                            </ListItemIcon>
                            <Typography color="textPrimary">Recent Rooms</Typography>
                        </MenuItem>
                        </>
                    )}

                    {props.player && <Divider />}

                    {authState == AuthState.SignedOut ? (
                        <>
                            <MenuItem onClick={handleSignInClicked}>
                                <ListItemIcon>
                                    <Login fontSize="small" />
                                </ListItemIcon>
                                <Typography color="textPrimary">Sign In</Typography>
                            </MenuItem>

                            <MenuItem onClick={handleSignUpClicked}>
                                <ListItemIcon>
                                    <PersonAdd fontSize="small" />
                                </ListItemIcon>
                                <Typography color="textPrimary">Sign Up</Typography>
                            </MenuItem>
                        </>
                    ) : (
                        
                        <MenuItem onClick={handleSignOutClicked}>
                            <ListItemIcon>
                                <Logout fontSize="small" />
                            </ListItemIcon>
                            <Typography color="textPrimary">Sign Out</Typography>
                        </MenuItem>
                    )}

                    {shouldShowSubscribe() && (
                        <>
                            <Divider />
                            <div style={{ 
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '16px'
                            }}>
                                <Button 
                                    variant="contained" 
                                    size="small" 
                                    color="secondary"
                                    onClick={handleSubscribeClicked}
                                    sx={{ 
                                        width: '80%',
                                    }}
                                > 
                                    Subscribe Now
                                </Button>
                            </div>
                        </>
                    )}
                </Menu>
            </Grid>
        </div>
    );
}